'use client';

import { useState, Children, isValidElement, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

const propertyLabels: Record<string, string> = {
  skycabin: 'Skycabin',
  levikka: 'Levikka',
  varakka: 'Varakka',
  pikupoikka: 'PikuPoikka',
  isopoikka: 'IsoPoikka',
  mestari: 'Mestari',
};

const filters = ['all', 'skycabin', 'levikka', 'varakka', 'pikupoikka', 'isopoikka', 'mestari'] as const;
type Filter = (typeof filters)[number];

export default function ReviewsFilterClient({
  children,
  allLabel = 'All',
  emptyText = 'No reviews yet. Check back soon!',
}: {
  children: ReactNode;
  allLabel?: string;
  emptyText?: string;
}) {
  const [filter, setFilter] = useState<Filter>('all');

  const filteredChildren = Children.toArray(children).filter((child) => {
    if (filter === 'all') return true;
    if (isValidElement(child)) {
      const props = child.props as { 'data-property'?: string };
      return props['data-property'] === filter;
    }
    return true;
  });

  return (
    <>
      {/* Filter tabs */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-10">
        <div className="flex flex-wrap justify-center gap-2">
          {filters.map((f) => {
            const isActive = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer',
                  isActive
                    ? 'bg-[#9B7B3A] text-white shadow-md'
                    : 'bg-white text-[#5A5A6E] hover:text-[#1A1A2E] hover:bg-slate-50 shadow-sm'
                )}
              >
                {f === 'all' ? allLabel : propertyLabels[f]}
              </button>
            );
          })}
        </div>
      </section>

      {/* Review grid with animation */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {filteredChildren.length === 0 ? (
              <p className="col-span-2 text-center text-gray-400 py-12">
                {emptyText}
              </p>
            ) : (
              filteredChildren
            )}
          </motion.div>
        </AnimatePresence>
      </section>
    </>
  );
}
